import { useEffect, useMemo, useState } from 'react';
import { ArrowLeft, Loader2, Play, PlugZap, RefreshCw, Save } from 'lucide-react';
import { logMcp } from '../mcpLogger';
import { listToolArgs, schemaToExampleJson } from '../toolSchema';
import type { ConnectionStatus, McpDiscoveredTool, McpInvokeResult, McpServerConnection } from '../types';

function statusClass(status: ConnectionStatus): string {
  switch (status) {
    case 'connected':
      return 'mcp-v2__pill mcp-v2__pill--connected';
    case 'failed':
      return 'mcp-v2__pill mcp-v2__pill--failed';
    case 'testing':
      return 'mcp-v2__pill mcp-v2__pill--testing';
    default:
      return 'mcp-v2__pill mcp-v2__pill--never';
  }
}

function statusLabel(status: ConnectionStatus): string {
  switch (status) {
    case 'connected':
      return 'Connected';
    case 'failed':
      return 'Failed';
    case 'testing':
      return 'Testing…';
    default:
      return 'Not tested';
  }
}

type Props = {
  connection: McpServerConnection;
  testing?: boolean;
  refreshing?: boolean;
  onBack: () => void;
  onTest: () => Promise<void>;
  onRefreshTools: () => Promise<void>;
  onSaveSelections: (
    toolEnabled: Record<string, boolean>,
    resourceEnabled: Record<string, boolean>
  ) => Promise<void>;
  onEdit: () => void;
  onInvokeTool: (
    toolId: string,
    toolName: string,
    args: Record<string, unknown>
  ) => Promise<McpInvokeResult>;
};

export default function ConnectionDetail({
  connection,
  testing = false,
  refreshing = false,
  onBack,
  onTest,
  onRefreshTools,
  onSaveSelections,
  onEdit,
  onInvokeTool,
}: Props) {
  const [toolEnabled, setToolEnabled] = useState<Record<string, boolean>>({});
  const [resourceEnabled, setResourceEnabled] = useState<Record<string, boolean>>({});
  const [savedFlash, setSavedFlash] = useState(false);
  const [saving, setSaving] = useState(false);
  const [testError, setTestError] = useState<string | null>(null);
  const [refreshError, setRefreshError] = useState<string | null>(null);
  const [refreshedFlash, setRefreshedFlash] = useState(false);
  const [invokeToolId, setInvokeToolId] = useState<string>('');
  const [invokeArgs, setInvokeArgs] = useState('{}');
  const [invokeBusy, setInvokeBusy] = useState(false);
  const [invokeResult, setInvokeResult] = useState<McpInvokeResult | null>(null);
  const [invokeError, setInvokeError] = useState<string | null>(null);

  useEffect(() => {
    const tools: Record<string, boolean> = {};
    for (const t of connection.tools) tools[t.id] = t.enabled;
    const resources: Record<string, boolean> = {};
    for (const r of connection.resources) resources[r.id] = r.enabled;
    setToolEnabled(tools);
    setResourceEnabled(resources);
    setSavedFlash(false);
    setTestError(null);
    setRefreshError(null);
  }, [connection]);

  useEffect(() => {
    if (!connection.tools.length) return;
    if (!connection.tools.some((t) => t.id === invokeToolId)) {
      setInvokeToolId(connection.tools[0].id);
    }
  }, [connection.tools, invokeToolId]);

  const endpointSummary = useMemo(() => {
    if (connection.transport === 'stdio') {
      const args = (connection.args ?? []).join(' ');
      return `${connection.command ?? ''} ${args}`.trim();
    }
    return connection.url ?? '—';
  }, [connection]);

  const selectedTool = connection.tools.find((t) => t.id === invokeToolId) ?? connection.tools[0];

  useEffect(() => {
    const tool = connection.tools.find((t) => t.id === invokeToolId) ?? connection.tools[0];
    if (!tool) {
      setInvokeArgs('{}');
      return;
    }
    setInvokeArgs(schemaToExampleJson(tool.inputSchema));
    logMcp('app', 'prefill_tool_args', { tool: tool.name });
  }, [invokeToolId, connection.tools]);

  const selectedToolCount = useMemo(
    () => connection.tools.filter((t) => toolEnabled[t.id]).length,
    [connection.tools, toolEnabled]
  );
  const allToolsSelected = connection.tools.length > 0 && selectedToolCount === connection.tools.length;
  const someToolsSelected = selectedToolCount > 0 && !allToolsSelected;

  const toggleAllTools = (enabled: boolean) => {
    const next: Record<string, boolean> = {};
    for (const tool of connection.tools) next[tool.id] = enabled;
    setToolEnabled(next);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await onSaveSelections(toolEnabled, resourceEnabled);
      setSavedFlash(true);
      window.setTimeout(() => setSavedFlash(false), 2000);
    } finally {
      setSaving(false);
    }
  };

  const busy = testing || refreshing;

  const handleTest = async () => {
    setTestError(null);
    setRefreshError(null);
    try {
      await onTest();
    } catch (err) {
      setTestError(err instanceof Error ? err.message : 'Test failed');
    }
  };

  const handleRefreshTools = async () => {
    setRefreshError(null);
    setTestError(null);
    setRefreshedFlash(false);
    logMcp('app', 'refresh_tools_click', { id: connection.id });
    try {
      await onRefreshTools();
      setRefreshedFlash(true);
      window.setTimeout(() => setRefreshedFlash(false), 4000);
      document.getElementById('mcp-v2-tools')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to update tools';
      setRefreshError(message);
      logMcp('error', 'refresh_tools_ui_failed', { id: connection.id, message });
    }
  };

  const handleInvoke = async () => {
    if (!selectedTool) return;
    setInvokeBusy(true);
    setInvokeError(null);
    setInvokeResult(null);
    let parsed: Record<string, unknown> = {};
    try {
      const raw = invokeArgs.trim() || '{}';
      const value = JSON.parse(raw) as unknown;
      if (!value || typeof value !== 'object' || Array.isArray(value)) {
        throw new Error('Arguments must be a JSON object');
      }
      parsed = value as Record<string, unknown>;
    } catch (err) {
      setInvokeBusy(false);
      setInvokeError(err instanceof Error ? err.message : 'Invalid JSON arguments');
      return;
    }
    try {
      const result = await onInvokeTool(selectedTool.id, selectedTool.name, parsed);
      setInvokeResult(result);
      if (!result.success) setInvokeError(result.error || 'Tool returned an error');
    } catch (err) {
      setInvokeError(err instanceof Error ? err.message : 'Tool invoke failed');
    } finally {
      setInvokeBusy(false);
    }
  };

  return (
    <div className="flex flex-col gap-4">
      <div>
        <button type="button" className="mcp-v2__btn mcp-v2__btn--ghost mcp-v2__detail-back" onClick={onBack}>
          <ArrowLeft className="w-4 h-4" aria-hidden />
          Back to connections
        </button>
        <div className="mcp-v2__header" style={{ marginTop: '0.5rem' }}>
          <div>
            <p className="mcp-v2__eyebrow">{connection.source} connection</p>
            <h2 className="mcp-v2__title">{connection.name}</h2>
            {connection.description ? (
              <p className="mcp-v2__subtitle">{connection.description}</p>
            ) : null}
            <div className="mcp-v2__meta" style={{ marginTop: 8 }}>
              <span className={statusClass(connection.status)}>{statusLabel(connection.status)}</span>
              {connection.code ? (
                <span className="mcp-v2__pill mcp-v2__pill--transport">{connection.code}</span>
              ) : null}
              <span className="mcp-v2__pill mcp-v2__pill--transport">{connection.transport}</span>
              {connection.tags.map((t) => (
                <span key={t} className="mcp-v2__tag">
                  {t}
                </span>
              ))}
            </div>
            {connection.lastError ? (
              <div className="mcp-v2__alert mcp-v2__alert--error" style={{ marginTop: 10 }}>
                {connection.lastError}
              </div>
            ) : null}
          </div>
          <div className="mcp-v2__actions">
            <button type="button" className="mcp-v2__btn mcp-v2__btn--outline" onClick={onEdit} disabled={busy}>
              Edit config
            </button>
            <button
              type="button"
              className="mcp-v2__btn mcp-v2__btn--outline"
              onClick={() => void handleRefreshTools()}
              disabled={busy}
            >
              {refreshing ? (
                <Loader2 className="w-4 h-4 animate-spin" aria-hidden />
              ) : (
                <RefreshCw className="w-4 h-4" aria-hidden />
              )}
              {refreshing ? 'Updating…' : 'Update tools'}
            </button>
            <button
              type="button"
              className="mcp-v2__btn mcp-v2__btn--primary"
              onClick={() => void handleTest()}
              disabled={busy}
            >
              {testing ? (
                <Loader2 className="w-4 h-4 animate-spin" aria-hidden />
              ) : (
                <PlugZap className="w-4 h-4" aria-hidden />
              )}
              {testing ? 'Testing…' : 'Test connection'}
            </button>
          </div>
        </div>
      </div>

      <div className="mcp-v2__panel mcp-v2__detail-section">
        <h3>Configuration</h3>
        <div className="mcp-v2__meta" style={{ flexDirection: 'column', alignItems: 'flex-start', gap: 6 }}>
          <span>
            <strong>Endpoint:</strong> {endpointSummary || '—'}
          </span>
          <span>
            <strong>Auth:</strong> {connection.authType}
            {connection.authType !== 'none' && connection.authConfigured ? ' · configured' : ''}
          </span>
          {connection.lastTestedAt ? (
            <span>
              <strong>Last tested:</strong> {new Date(connection.lastTestedAt).toLocaleString()}
            </span>
          ) : null}
        </div>
        {testError ? (
          <div className="mcp-v2__alert mcp-v2__alert--error" style={{ marginTop: 12 }}>
            {testError}
          </div>
        ) : null}
        {refreshError ? (
          <div className="mcp-v2__alert mcp-v2__alert--error" style={{ marginTop: 12 }}>
            {refreshError}
          </div>
        ) : null}
        {refreshedFlash ? (
          <div className="mcp-v2__alert mcp-v2__alert--ok" style={{ marginTop: 12 }}>
            Tools refreshed. Select one or all, then save to update this connection.
          </div>
        ) : null}
        {connection.status === 'connected' && !connection.lastError && !refreshedFlash ? (
          <div className="mcp-v2__alert mcp-v2__alert--ok" style={{ marginTop: 12 }}>
            Connection succeeded. Review tools below, save which ones stay enabled, then test a tool.
          </div>
        ) : null}
      </div>

      <div className="mcp-v2__detail-grid">
        <div id="mcp-v2-tools" className="mcp-v2__panel mcp-v2__detail-section">
          <div className="flex items-center justify-between gap-2 mb-2">
            <h3 style={{ margin: 0 }}>Tools ({connection.tools.length})</h3>
            {connection.tools.length > 0 ? (
              <label className="mcp-v2__check mcp-v2__select-all">
                <input
                  type="checkbox"
                  checked={allToolsSelected}
                  ref={(el) => {
                    if (el) el.indeterminate = someToolsSelected;
                  }}
                  onChange={(e) => toggleAllTools(e.target.checked)}
                  aria-label="Select all tools"
                />
                Select all
                <span className="mcp-v2__select-all-count">
                  {selectedToolCount}/{connection.tools.length}
                </span>
              </label>
            ) : null}
          </div>
          {connection.tools.length === 0 ? (
            <div>
              <p style={{ margin: 0, color: '#71717a', fontSize: '0.8125rem' }}>
                Catalog counts are an estimate. Update tools to fetch the live list, then select one
                or all and save. Discovered tools are stored on this connection — they are not copied
                from the catalog.
              </p>
              <button
                type="button"
                className="mcp-v2__btn mcp-v2__btn--primary"
                style={{ marginTop: 12 }}
                onClick={() => void handleRefreshTools()}
                disabled={busy}
              >
                {refreshing ? (
                  <Loader2 className="w-4 h-4 animate-spin" aria-hidden />
                ) : (
                  <RefreshCw className="w-4 h-4" aria-hidden />
                )}
                {refreshing ? 'Updating…' : 'Update tools'}
              </button>
            </div>
          ) : (
            connection.tools.map((t) => (
              <ToolRow
                key={t.id}
                tool={t}
                checked={toolEnabled[t.id] ?? false}
                onCheckedChange={(checked) =>
                  setToolEnabled((prev) => ({ ...prev, [t.id]: checked }))
                }
              />
            ))
          )}
        </div>

        <div className="mcp-v2__panel mcp-v2__detail-section">
          <div className="flex items-center justify-between gap-2 mb-2">
            <h3 style={{ margin: 0 }}>Resources ({connection.resources.length})</h3>
          </div>
          {connection.resources.length === 0 ? (
            <p style={{ margin: 0, color: '#71717a', fontSize: '0.8125rem' }}>
              Catalog resource counts are an estimate. Test this connection to load live resources.
              Playwright and some other servers advertise none.
            </p>
          ) : (
            connection.resources.map((r) => (
              <label key={r.id} className="mcp-v2__discover-item mcp-v2__check">
                <input
                  type="checkbox"
                  checked={resourceEnabled[r.id] ?? false}
                  onChange={(e) =>
                    setResourceEnabled((prev) => ({ ...prev, [r.id]: e.target.checked }))
                  }
                />
                <span>
                  <strong>{r.name}</strong>
                  <span>{r.uri}</span>
                  <span>
                    {r.mimeType}
                    {r.description ? ` · ${r.description}` : ''}
                  </span>
                </span>
              </label>
            ))
          )}
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <button
          type="button"
          className="mcp-v2__btn mcp-v2__btn--primary"
          onClick={() => void handleSave()}
          disabled={saving || busy || (connection.tools.length === 0 && connection.resources.length === 0)}
        >
          <Save className="w-4 h-4" aria-hidden />
          {saving ? 'Saving…' : 'Save selections'}
        </button>
        {savedFlash ? (
          <span className="mcp-v2__alert mcp-v2__alert--ok" style={{ padding: '0.4rem 0.75rem' }}>
            Selections saved
          </span>
        ) : null}
      </div>

      <div className="mcp-v2__panel mcp-v2__detail-section">
        <h3>Test a tool</h3>
        {connection.tools.length === 0 ? (
          <p style={{ margin: 0, color: '#71717a', fontSize: '0.8125rem' }}>
            Discover tools first, then invoke one with JSON arguments.
          </p>
        ) : (
          <div className="flex flex-col gap-3">
            <div className="mcp-v2__field">
              <label htmlFor="mcp-v2-invoke-tool">Tool</label>
              <select
                id="mcp-v2-invoke-tool"
                className="mcp-v2__select"
                style={{ width: '100%' }}
                value={selectedTool?.id ?? ''}
                onChange={(e) => setInvokeToolId(e.target.value)}
              >
                {connection.tools.map((t) => (
                  <option key={t.id} value={t.id}>
                    {t.name}
                  </option>
                ))}
              </select>
            </div>
            {selectedTool?.inputSchemaSummary ? (
              <p style={{ margin: 0, color: '#a1a1aa', fontSize: '0.75rem' }}>
                Schema: {selectedTool.inputSchemaSummary}
              </p>
            ) : null}
            <div className="mcp-v2__field">
              <label htmlFor="mcp-v2-invoke-args">Arguments (JSON)</label>
              <textarea
                id="mcp-v2-invoke-args"
                className="mcp-v2__textarea"
                rows={4}
                value={invokeArgs}
                onChange={(e) => setInvokeArgs(e.target.value)}
                placeholder="{}"
              />
            </div>
            <div>
              <button
                type="button"
                className="mcp-v2__btn mcp-v2__btn--outline"
                onClick={() => void handleInvoke()}
                disabled={invokeBusy || !selectedTool}
              >
                {invokeBusy ? (
                  <Loader2 className="w-4 h-4 animate-spin" aria-hidden />
                ) : (
                  <Play className="w-4 h-4" aria-hidden />
                )}
                {invokeBusy ? 'Invoking…' : 'Run tool'}
              </button>
            </div>
            {invokeError ? <div className="mcp-v2__alert mcp-v2__alert--error">{invokeError}</div> : null}
            {invokeResult ? (
              <pre className="mcp-v2__invoke-out">
                {JSON.stringify(
                  {
                    success: invokeResult.success,
                    latencyMs: invokeResult.latencyMs,
                    result: invokeResult.result,
                    error: invokeResult.error || undefined,
                  },
                  null,
                  2
                )}
              </pre>
            ) : null}
          </div>
        )}
      </div>
    </div>
  );
}

function ToolRow({
  tool,
  checked,
  onCheckedChange,
}: {
  tool: McpDiscoveredTool;
  checked: boolean;
  onCheckedChange: (checked: boolean) => void;
}) {
  const args = listToolArgs(tool.inputSchema);
  return (
    <div className="mcp-v2__discover-item">
      <label className="mcp-v2__check mcp-v2__tool-enable">
        <input
          type="checkbox"
          checked={checked}
          onChange={(e) => onCheckedChange(e.target.checked)}
          aria-label={`Enable ${tool.name}`}
        />
      </label>
      <div className="mcp-v2__tool-body">
        <strong>{tool.name}</strong>
        {tool.description ? <p className="mcp-v2__tool-desc">{tool.description}</p> : null}
        {args.length === 0 ? (
          <p className="mcp-v2__tool-no-args">No arguments</p>
        ) : (
          <ul className="mcp-v2__arg-list">
            {args.map((arg) => (
              <li key={arg.name}>
                <code>{arg.name}</code>
                <span className="mcp-v2__arg-type">{arg.type}</span>
                {arg.required ? <span className="mcp-v2__arg-req">required</span> : <span>optional</span>}
                {arg.description ? <span className="mcp-v2__arg-desc">{arg.description}</span> : null}
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
